import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Code2, Copy, ExternalLink, FileQuestion, Layers } from 'lucide-react';
import { Badge } from '../ui/Badge.jsx';
import { Button } from '../ui/Button.jsx';
import { Drawer } from '../ui/Drawer.jsx';
import { EmptyState } from '../ui/EmptyState.jsx';
import { Skeleton } from '../ui/Skeleton.jsx';
import { useToast } from '../../context/ToastContext';
import { formatSourceReference } from '../chat/trust';
import { scrollBehavior } from '../../utils/motion';
import EtchAction from './EtchAction.jsx';

const KIND_ICON = {
  code: Code2,
  ckb: Code2,
  document: BookOpen,
  catalog: Layers,
  unknown: FileQuestion,
};

const KIND_LABEL_KEY = {
  code: 'chat.sourceKindCode',
  ckb: 'chat.sourceKindCkb',
  document: 'chat.sourceKindDocument',
  catalog: 'chat.sourceKindCatalog',
  unknown: 'chat.sourceKindUnknown',
};

const STATE_LABEL_KEY = {
  stale: 'states.staleSource',
  unreachable: 'chat.sourceUnreachable',
};

/** Where "öffnen" takes the user for each source kind. */
function targetRoute(source) {
  switch (source?.kind) {
    case 'document':
      return '/admin/documents';
    case 'catalog':
      return '/missions';
    case 'code':
    case 'ckb':
      return '/knowledge';
    default:
      return null;
  }
}

function SourceCard({ source, active, onSelect, onOpen, onCopy, registerRef }) {
  const { t } = useTranslation();
  const Icon = KIND_ICON[source.kind] || KIND_ICON.unknown;
  const unreachable = source.state === 'unreachable';
  const stateLabel = STATE_LABEL_KEY[source.state] ? t(STATE_LABEL_KEY[source.state]) : null;
  const title = source.title || source.ref || t('chat.sourceUntitled');

  return (
    <li
      className="etch-source-card"
      data-state={source.state}
      data-kind={source.kind}
      data-active={active || undefined}
      data-cited={source.cited || undefined}
      tabIndex={-1}
      ref={(node) => registerRef?.(source.id, node)}
      aria-current={active ? 'true' : undefined}
    >
      <div className="etch-source-card__head">
        {source.index != null && (
          <span className="etch-source-card__index" aria-hidden="true">
            {source.index}
          </span>
        )}
        <button
          type="button"
          className="etch-source-card__title"
          onClick={() => onSelect?.(source.id)}
          aria-pressed={active}
        >
          <Icon size={14} aria-hidden="true" />
          <span>{title}</span>
        </button>
      </div>

      <p className="etch-source-card__meta">
        <Badge>{t(KIND_LABEL_KEY[source.kind] || KIND_LABEL_KEY.unknown)}</Badge>
        {source.locator && <span className="etch-source-card__locator">{source.locator}</span>}
        {stateLabel && (
          <Badge tone={unreachable ? 'danger' : 'warning'}>{stateLabel}</Badge>
        )}
      </p>

      {source.snippet ? (
        <blockquote className="etch-source-card__snippet">{source.snippet}</blockquote>
      ) : (
        <p className="etch-source-card__snippet" data-empty="true">
          {unreachable ? t('chat.sourceUnreachableBody') : t('chat.sourceNoSnippet')}
        </p>
      )}

      <div className="etch-source-card__actions">
        <Button
          variant="ghost"
          size="compact"
          onClick={() => onOpen?.(source)}
          disabled={unreachable || !targetRoute(source)}
        >
          <ExternalLink size={13} aria-hidden="true" />
          <span>{t('chat.sourceOpen')}</span>
        </Button>
        <Button variant="ghost" size="compact" onClick={() => onCopy?.(source)}>
          <Copy size={13} aria-hidden="true" />
          <span>{t('chat.sourceCopyRef')}</span>
        </Button>
        <EtchAction
          text={source.snippet || source.title || ''}
          sources={[source]}
          defaultSourceId={source.id}
          topic={source.title || null}
        />
      </div>
    </li>
  );
}

/**
 * The source panel of the current answer (design spec §9.1, §10.2).
 *
 * It fills from the `chat:sources` event, which arrives before the first
 * chunk — so sources are readable while the answer is still streaming. Sources
 * that could not be reached stay in the list with an explicit state instead of
 * disappearing (§11.2).
 *
 * @param {object} props
 * @param {object[]} props.sources - resolved descriptors (see chat/trust.js).
 * @param {string|null} [props.activeId]
 * @param {(id: string) => void} [props.onSelect]
 * @param {() => void} [props.onClose]
 * @param {boolean} [props.loading] - sources not delivered yet.
 * @param {'panel'|'drawer'} [props.variant='panel']
 * @param {boolean} [props.open=true] - drawer visibility.
 */
export function SourcePanel({
  sources = [],
  activeId = null,
  onSelect,
  onClose,
  loading = false,
  variant = 'panel',
  open = true,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const cardRefs = useRef(new Map());
  const focusedIdRef = useRef(null);

  const registerRef = (id, node) => {
    if (node) cardRefs.current.set(id, node);
    else cardRefs.current.delete(id);
  };

  // Bringing the active card into view is safe on every change of the list.
  // Moving *focus* is not: `sources.length` grows on every stream tick, so
  // focusing here would yank the caret out of the composer mid-answer — an
  // unsolicited focus change (WCAG 3.2.1/3.2.2, 2.4.3). Focus therefore only
  // follows a change of the selection itself.
  useEffect(() => {
    if (!activeId || !open) return;
    const node = cardRefs.current.get(activeId);
    if (!node) return;
    node.scrollIntoView({ block: 'nearest', behavior: scrollBehavior() });
    if (focusedIdRef.current === activeId) return;
    focusedIdRef.current = activeId;
    node.focus({ preventScroll: true });
  }, [activeId, open, sources.length]);

  useEffect(() => {
    if (!activeId || !open) focusedIdRef.current = null;
  }, [activeId, open]);

  const handleCopy = async (source) => {
    const reference = formatSourceReference(source);
    try {
      await navigator.clipboard.writeText(reference);
      toast.success(t('chat.sourceCopied'));
    } catch (err) {
      toast.error(err.message || t('states.error'));
    }
  };

  const handleOpen = (source) => {
    const route = targetRoute(source);
    if (route) navigate(route);
  };

  const body = (
    <>
      {loading && sources.length === 0 && (
        <div className="etch-source-panel__loading">
          <p role="status">{t('states.loading')}</p>
          <Skeleton height="4rem" />
          <Skeleton height="4rem" />
        </div>
      )}

      {!loading && sources.length === 0 && (
        <EmptyState title={t('states.noSource')}>{t('chat.sourcesEmptyBody')}</EmptyState>
      )}

      {sources.length > 0 && (
        <ul className="etch-source-panel__list">
          {sources.map((source) => (
            <SourceCard
              key={source.id}
              source={source}
              active={source.id === activeId}
              onSelect={onSelect}
              onOpen={handleOpen}
              onCopy={handleCopy}
              registerRef={registerRef}
            />
          ))}
        </ul>
      )}
    </>
  );

  if (variant === 'drawer') {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        side="end"
        title={t('chat.sources')}
        closeLabel={t('a11y.closeDialog')}
        className="etch-source-panel"
      >
        {body}
      </Drawer>
    );
  }

  if (!open) return null;

  return (
    <aside className="etch-source-panel" aria-label={t('chat.sources')}>
      <div className="etch-source-panel__header">
        <h2 className="etch-source-panel__title">{t('chat.sources')}</h2>
        {onClose && (
          <Button variant="ghost" size="compact" onClick={onClose}>
            {t('chat.sourcesHide')}
          </Button>
        )}
      </div>
      {body}
    </aside>
  );
}

export default SourcePanel;
